import { createContext, useContext, useEffect, useState } from "react";
import { Card, Menu, Select, Divider, Input, Button, message } from "antd";
import {
  UserOutlined, TeamOutlined, SafetyCertificateOutlined, SettingOutlined,
  RobotOutlined, ApiOutlined, ThunderboltOutlined, CloudServerOutlined,
  KeyOutlined, AppstoreOutlined, StarOutlined, PlusOutlined, DatabaseOutlined,
} from "@ant-design/icons";
import { Outlet, useNavigate, useLocation, useParams } from "react-router";
import { useTranslation } from "react-i18next";
import { useAuthStore } from "@/stores/auth";
import { useManagementStore } from "@/stores/management";
import { orgService } from "@/services/organization";

const CardExtraContext = createContext<{ setExtra: (node: React.ReactNode) => void }>({
  setExtra: () => {},
});

export function useCardExtra(node: React.ReactNode, deps: React.DependencyList = []) {
  const { setExtra } = useContext(CardExtraContext);
  useEffect(() => {
    setExtra(node);
    return () => setExtra(null);
  }, deps);
}

const P = {
  ROLE_MANAGE: "OrgRoleManage",
  MEMBER_INVITE: "OrgMemberInvite",
  MEMBER_REMOVE: "OrgMemberRemove",
  SSO_CONFIG: "OrgSsoConfig",
};

export function ManagementLayout() {
  const { t } = useTranslation("management");
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const { user } = useAuthStore();
  const { orgs, setOrgs, context, setContext, permissions, setPermissions, hasPermission } = useManagementStore();
  const [cardExtra, setCardExtra] = useState<React.ReactNode>(null);
  const [newOrgName, setNewOrgName] = useState("");
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    orgService.list().then(setOrgs).catch(() => {});
  }, [setOrgs]);

  // Fetch permissions when org context changes
  useEffect(() => {
    if (context?.type === "org") {
      orgService.myPermissions(context.org.id)
        .then(setPermissions)
        .catch(() => setPermissions([]));
    } else if (context?.type === "system") {
      setPermissions([]);
    }
  }, [context, setPermissions]);

  // Sync context from URL
  useEffect(() => {
    if (location.pathname.startsWith("/management/system")) {
      if (context?.type !== "system") setContext({ type: "system" });
    } else if (params.orgId) {
      const org = orgs.find((o) => o.id === params.orgId);
      if (org && (context?.type !== "org" || context.org.id !== org.id)) {
        setContext({ type: "org", org });
      }
    } else if (orgs.length > 0 && !context) {
      if (user?.is_system_admin) {
        setContext({ type: "system" });
        navigate("/management/system/users", { replace: true });
      } else {
        setContext({ type: "org", org: orgs[0] });
        navigate(`/management/org/${orgs[0].id}/members`, { replace: true });
      }
    }
  }, [location.pathname, params.orgId, orgs, context, user, setContext, navigate]);

  const contextValue = context?.type === "system" ? "system" : context?.type === "org" ? context.org.id : undefined;

  const handleContextChange = (value: string) => {
    if (value === "system") {
      setContext({ type: "system" });
      navigate("/management/system/users");
    } else {
      const org = orgs.find((o) => o.id === value);
      if (org) {
        setContext({ type: "org", org });
        navigate(`/management/org/${org.id}/members`);
      }
    }
  };

  const handleCreateOrg = async () => {
    if (!newOrgName.trim()) return;
    setCreating(true);
    try {
      const org = await orgService.create(newOrgName.trim());
      setOrgs([...orgs, org]);
      setNewOrgName("");
      message.success(t("common:success"));
      setContext({ type: "org", org });
      navigate(`/management/org/${org.id}/members`);
    } catch {
      message.error(t("common:error"));
    } finally {
      setCreating(false);
    }
  };

  const systemMenuItems = [
    { key: "/management/system/users", icon: <UserOutlined />, label: t("menu.users") },
    { key: "/management/system/orgs", icon: <AppstoreOutlined />, label: t("menu.orgs") },
  ];

  const orgId = context?.type === "org" ? context.org.id : "";

  // Filter org menu items based on permissions
  const orgMenuItems = [
    hasPermission(P.ROLE_MANAGE) && { key: `/management/org/${orgId}/settings`, icon: <SettingOutlined />, label: t("menu.org_settings") },
    hasPermission(P.ROLE_MANAGE) && { key: `/management/org/${orgId}/roles`, icon: <SafetyCertificateOutlined />, label: t("menu.roles") },
    { key: `/management/org/${orgId}/members`, icon: <TeamOutlined />, label: t("menu.members") },
    { type: "divider" as const },
    { key: `/management/org/${orgId}/agents`, icon: <RobotOutlined />, label: t("menu.agents") },
    { key: `/management/org/${orgId}/mcps`, icon: <ApiOutlined />, label: t("menu.mcps") },
    { key: `/management/org/${orgId}/skills`, icon: <ThunderboltOutlined />, label: t("menu.skills") },
    { key: `/management/org/${orgId}/providers`, icon: <DatabaseOutlined />, label: t("menu.providers") },
    { key: `/management/org/${orgId}/clients`, icon: <CloudServerOutlined />, label: t("menu.clients") },
    { type: "divider" as const },
    hasPermission(P.SSO_CONFIG) && { key: `/management/org/${orgId}/sso`, icon: <KeyOutlined />, label: t("menu.sso") },
  ].filter(Boolean) as typeof systemMenuItems;

  const menuItems = context?.type === "system" ? systemMenuItems : orgMenuItems;

  const currentTitle = [...systemMenuItems, ...orgMenuItems]
    .find((item) => "key" in item && item.key === location.pathname)?.label as string | undefined;

  const selectOptions = [
    ...(user?.is_system_admin
      ? [{ label: <span><StarOutlined style={{ marginRight: 6 }} />{t("context.system")}</span>, value: "system" }]
      : []),
    ...orgs.map((o) => ({ label: o.name, value: o.id })),
  ];

  return (
    <div style={{ display: "flex", height: "100%" }}>
      <div style={{
        width: 220, flexShrink: 0, display: "flex", flexDirection: "column",
        background: "var(--sider-bg, #fff)", borderRight: "1px solid var(--border, #E5E7EB)",
      }}>
        <div style={{ padding: "16px 12px 8px" }}>
          <Select
            value={contextValue}
            onChange={handleContextChange}
            style={{ width: "100%" }}
            options={selectOptions}
            dropdownRender={(menu) => (
              <>
                {menu}
                <Divider style={{ margin: "8px 0" }} />
                <div style={{ padding: "0 8px 8px", display: "flex", gap: 4 }}>
                  <Input
                    size="small"
                    placeholder={t("context.create_org.placeholder")}
                    value={newOrgName}
                    onChange={(e) => setNewOrgName(e.target.value)}
                    onPressEnter={handleCreateOrg}
                  />
                  <Button
                    size="small"
                    type="text"
                    icon={<PlusOutlined />}
                    loading={creating}
                    onClick={handleCreateOrg}
                  />
                </div>
              </>
            )}
          />
        </div>

        <Menu
          mode="inline"
          selectedKeys={[location.pathname]}
          items={menuItems}
          onClick={({ key }) => navigate(key)}
          style={{ border: "none", flex: 1 }}
        />
      </div>

      <div style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
        <Card
          size="small"
          title={currentTitle}
          extra={cardExtra}
          style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", borderRadius: 0 }}
          styles={{ body: { flex: 1, overflow: "auto" } }}
        >
          <CardExtraContext.Provider value={{ setExtra: setCardExtra }}>
            <Outlet />
          </CardExtraContext.Provider>
        </Card>
      </div>
    </div>
  );
}
